import type {
  BankAccount,
  BeneficiaryDetail,
  BeneficiaryDetailResponse,
} from "@/domain/beneficiary";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject, nestedSlot, type Merge } from "../cache/cacheSlots";
import { aggregateSlot, flatAggregates } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  byUuid,
  distinctPages,
  filled,
  listedStatus,
  overlaps,
  viewSlot,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

export type BeneficiaryPatch = Partial<BeneficiaryDetail>;

export type BankAccountPatch = Partial<BankAccount>;

interface BeneficiaryPage {
  beneficiaries: BeneficiaryDetail[];
  total: number;
}

function isBeneficiaryDetail(value: unknown): value is BeneficiaryDetailResponse {
  return (
    isObject(value) &&
    isObject(value.beneficiary) &&
    typeof value.beneficiary.uuid === "string" &&
    Array.isArray(value.bank_accounts)
  );
}

const beneficiaryPage: ViewShape<BeneficiaryPage, BeneficiaryDetail, string> = {
  isPage: (value): value is BeneficiaryPage =>
    isObject(value) && Array.isArray(value.beneficiaries) && typeof value.total === "number",
  records: (page) => page.beneficiaries,
  withRecords: (page, beneficiaries, countDelta) => ({
    ...page,
    beneficiaries,
    total: page.total + countDelta,
  }),
  idOf: (record) => record.uuid,
  total: (page) => page.total,
};

export const distinctBeneficiaryPages = distinctPages(beneficiaryPage);

const time = (value: string) => Date.parse(value);

export const beneficiaryRules: ViewRules<BeneficiaryDetail> = {
  accepts: {
    status: listedStatus((row) => row.status),
    sector: overlaps((row) => row.main_activities),
    tag: overlaps((row) => row.tags.map((tag) => tag.uuid)),
  },
  narrowing: ["search", "incentive_type", "company_type", "has_current_year_quota"],
  opaque: { search: (row) => [row.name, row.email, row.tax_id] },
  order: {
    created_at: (row) => time(row.created_at),
    total_projects_count: (row) => row.total_projects_count ?? null,
  },
  tieBreak: byUuid("asc", (row) => row.uuid),
};

const beneficiaryAggregates = flatAggregates<BeneficiaryDetail>({
  counts: {
    "status:active": (row) => row.status === "active",
    "status:inactive": (row) => row.status === "inactive",
    "city:filled": (row) => filled(row.city),
    "city:empty": (row) => !filled(row.city),
    "email:filled": (row) => filled(row.email),
    "email:empty": (row) => !filled(row.email),
    "primary_contact:filled": (row) => filled(row.primary_contact?.full_name),
    "primary_contact:empty": (row) => !filled(row.primary_contact?.full_name),
  },
  distributions: {
    "sector:distribution": (row) => row.main_activities,
    "tag:distribution": (row) => row.tags.map((tag) => tag.uuid),
  },
  ratios: { "status:pct_active": (row) => row.status === "active" },
  emptyDistribution: {},
});

/** The promoters list and its footers, for any change that reaches a list row. */
export function beneficiaryListSlots<TPatch>(
  orgId: string,
  merge: Merge<BeneficiaryDetail, TPatch>,
) {
  return [
    viewSlot({
      prefix: queryKeys.beneficiaries.lists(orgId),
      shape: beneficiaryPage,
      merge,
      parse: queryKeys.beneficiaries.parseList,
      rules: beneficiaryRules,
    }),
    aggregateSlot<Record<string, unknown>, BeneficiaryDetail, string, TPatch>({
      prefix: queryKeys.beneficiaries.aggregatesAll(orgId),
      rules: beneficiaryRules,
      parse: queryKeys.beneficiaries.parseAggregates,
      ...beneficiaryAggregates,
      sizeFrom: {
        prefix: queryKeys.beneficiaries.lists(orgId),
        parse: queryKeys.beneficiaries.parseList,
        total: (page) => (beneficiaryPage.isPage(page) ? page.total : undefined),
      },
    }),
  ];
}

const mergeRow = (record: BeneficiaryDetail, patch: BeneficiaryPatch): BeneficiaryDetail => ({
  ...record,
  ...patch,
});

export const beneficiaryCache = defineEntityCache<string, BeneficiaryPatch, BeneficiaryDetail>({
  entity: InvalidationEntity.BENEFICIARY,
  row: mergeRow,
  slots: (orgId) => [
    ...beneficiaryListSlots(orgId, mergeRow),
    detailSlot({
      prefix: queryKeys.beneficiaries.details(orgId),
      isRecord: isBeneficiaryDetail,
      idOf: (detail) => detail.beneficiary.uuid,
      merge: (detail, patch) => ({ ...detail, beneficiary: { ...detail.beneficiary, ...patch } }),
    }),
  ],
  records: (orgId, uuid) => [
    queryKeys.beneficiaries.detail(orgId, uuid),
    queryKeys.beneficiaries.documents(orgId, uuid),
    queryKeys.beneficiaries.documentsByQuota(orgId, uuid),
    queryKeys.beneficiaries.fiscalQuotasByBeneficiary(orgId, uuid),
  ],
});

/** Bank accounts only live inside the promoter detail. */
export const bankAccountCache = defineEntityCache<string, BankAccountPatch>({
  entity: InvalidationEntity.BENEFICIARY,
  slots: (orgId) => [
    nestedSlot({
      prefix: queryKeys.beneficiaries.details(orgId),
      isParent: isBeneficiaryDetail,
      children: (detail) => detail.bank_accounts,
      withChildren: (detail, bank_accounts) => ({ ...detail, bank_accounts }),
      idOf: (account) => account.uuid,
      merge: (account, patch) => ({ ...account, ...patch }),
    }),
  ],
  records: () => [],
});
